import { createBrowserRouter, Navigate } from "react-router";

import { AuthLayout } from "@/components/layouts/auth-layout";
import { Layout } from "@/components/layouts/layout";
import { AdminRoute, OnboardingRoute, ProtectedRoute, PublicRoute } from "@/components/layouts/layout-routes";
import { SelectCompByRole } from "@/components/select-comp-by-role";
import { OAuthCallbackPage, OAuthSignInPage } from "@/pages/auth/oauth-callback-page";
import { SignInPage } from "@/pages/auth/signin/sign-in-page";
import { SignupAsClub } from "@/pages/auth/signup/club-signup-page";
import { SignupAsPlayer } from "@/pages/auth/signup/player-signup-page";
import { SelectSignupRole } from "@/pages/auth/signup/select-signup-role-page";
import { ClubList } from "@/pages/club-list/club-list";
import { OnboardingPage } from "@/pages/onboarding/onboarding-page";
import { ClubProfile } from "@/pages/profile/club-profile-page";
import { PlayerProfile } from "@/pages/profile/player-profile-page";
import { routes } from "@/routes";

import { Providers } from "./components/providers";
import { UserContextWrapper } from "./context/user-context-wrapper";
import { AdminPage } from "./pages/admin/admin-page";
import { ClubFavorites } from "./pages/favorites/club-favorites";
import { PlayerFavorites } from "./pages/favorites/player-favorites";
import { Messages } from "./pages/messages/messages";
import { NotFoundPage } from "./pages/not-found-page";
import { PlansPage } from "./pages/plans";
import { PlayerList } from "./pages/player-list/player-list";
import { TournamentList } from "./pages/tournament-list/tournament-list";
import { TournamentPage } from "./pages/tournaments/tournament-page";

export const router = createBrowserRouter([
  {
    element: <Providers />,
    children: [
      { path: routes.oauthCallback, element: <AuthLayout />, children: [{ index: true, element: <OAuthCallbackPage /> }] },
      { path: routes.oauthSignIn, element: <AuthLayout />, children: [{ index: true, element: <OAuthSignInPage /> }] },
      {
        element: <PublicRoute />,
        children: [
          {
            element: <AuthLayout />,
            path: routes.signup,
            children: [
              { index: true, element: <SelectSignupRole /> },
              {
                path: routes.signupPlayer,
                element: <SignupAsPlayer />,
              },
              {
                path: routes.signupClub,
                element: <SignupAsClub />,
              },
              {
                path: routes.signin,
                element: <SignInPage />,
              },
            ],
          },
        ],
      },
      {
        element: <ProtectedRoute />,
        children: [{
          children: [
            {
              element: <OnboardingRoute />,
              children: [
                {
                  path: routes.onboarding,
                  element: <AuthLayout />,
                  children: [
                    {
                      index: true,
                      element: <OnboardingPage />,
                    },
                  ],
                },
              ],
            },
            {
              element: (
                <UserContextWrapper>
                  <Layout />
                </UserContextWrapper>
              ),
              children: [
                {
                  path: routes.root,
                  element: <Navigate to={routes.profile} />,
                },
                {
                  path: routes.admin,
                  element: <AdminRoute />,
                  children: [
                    {
                      index: true,
                      element: <AdminPage />,
                    },
                    {
                      path: routes.adminPlayerProfile,
                      element: <PlayerProfile />,
                    },
                    {
                      path: routes.adminClubProfile,
                      element: <ClubProfile />,
                    },
                  ],
                },
                {
                  path: routes.profile,
                  element: <SelectCompByRole clubComp={<ClubProfile />} playerComp={<PlayerProfile />} />,
                },
                {
                  path: routes.players,
                  // accès uniquement pour les clubs, plus les admins
                  element: <SelectCompByRole clubComp={<PlayerList />} playerComp={<NotFoundPage />} adminComp={<PlayerList />} />,
                },
                {
                  path: routes.clubs,
                  // accès uniquement pour les joueurs
                  element: <SelectCompByRole clubComp={<NotFoundPage />} playerComp={<ClubList />} />,
                },
                {
                  path: routes.club,
                  // accès uniquement pour les joueurs
                  element: <SelectCompByRole clubComp={<NotFoundPage />} playerComp={<ClubProfile />} />,
                },
                {
                  path: routes.player,
                  // accès uniquement pour les clubs, plus les admins
                  element: <SelectCompByRole clubComp={<PlayerProfile />} playerComp={<NotFoundPage />} adminComp={<PlayerProfile />} />,
                },
                {
                  path: routes.favorites,
                  element: <SelectCompByRole clubComp={<ClubFavorites />} playerComp={<PlayerFavorites />} />,
                },
                {
                  path: routes.tournaments,
                  element: <TournamentList />,
                },
                {
                  path: routes.tournament,
                  element: <TournamentPage />,
                },
                {
                  path: routes.messages,
                  element: <Messages />,
                },
                {
                  path: routes.plans,
                  element: <PlansPage />,
                },
                {
                  path: "*",
                  element: <NotFoundPage />,
                },
              ],
            },
          ],
        },
        ],
      },
    ],
  },
]);
